const documentTokenPositions = new WeakMap()

function dataFormatError(message) {
  const error = new Error(`Invalid static DocLing index: ${message}`)
  error.code = 'STATIC_DATA_FORMAT'
  return error
}

function compileRegex(pattern, caseSensitive, label) {
  const source = String(pattern || '')
  if (source.length > 500 || /(\([^)]*[+*][^)]*\))[+*{]/.test(source)) {
    const error = new Error(`Unsafe ${label || 'search'} regular expression`)
    error.code = 'UNSAFE_SEARCH_REGEX'
    throw error
  }
  try {
    return new RegExp(source, caseSensitive ? '' : 'i')
  } catch (cause) {
    const error = new Error(`Invalid ${label || 'search'} regular expression: ${cause.message}`)
    error.code = 'INVALID_SEARCH_REGEX'
    throw error
  }
}

function normalizeDocument(documentId, value) {
  if (Array.isArray(value)) {
    if (![7, 12].includes(value.length) ||
        !Array.isArray(value[5]) || !Array.isArray(value[6])) {
      throw dataFormatError(`document ${documentId} does not match the supported tuple schema`)
    }
    if (value[5].length !== value[6].length) {
      throw dataFormatError(`document ${documentId} has mismatched token/type sequences`)
    }
    if (value.length === 12 && value.slice(7).some(
      sequence => !Array.isArray(sequence) || sequence.length !== value[6].length
    )) {
      throw dataFormatError(`document ${documentId} has mismatched metadata sequences`)
    }
    return {
      id: String(documentId),
      corpus: value[0] || null,
      editionSigle: value[1] || value[0] || '',
      date: value[2] || null,
      scriptoria: Array.isArray(value[3]) ? value[3] : [],
      scripta: Array.isArray(value[4]) ? value[4] : [],
      typeIndices: value[5],
      tokenIds: value[6].map(String),
      divisions: Array.isArray(value[7]) ? value[7] : [],
      languages: Array.isArray(value[8]) ? value[8] : [],
      headIds: Array.isArray(value[9]) ? value[9] : [],
      lemmaIds: Array.isArray(value[10]) ? value[10] : [],
      hccIds: Array.isArray(value[11]) ? value[11] : [],
      hasMetadata: value.length >= 12,
    }
  }

  if (value && typeof value === 'object') {
    const typeIndices = value.typeIndices || value.types
    const tokenIds = value.tokenIds || value.tokens
    if (!Array.isArray(typeIndices) || !Array.isArray(tokenIds)) {
      throw dataFormatError(`document ${documentId} is missing token/type sequences`)
    }
    if (typeIndices.length !== tokenIds.length) {
      throw dataFormatError(`document ${documentId} has mismatched token/type sequences`)
    }
    return {
      id: String(documentId),
      corpus: value.corpus || null,
      editionSigle: value.editionSigle || value.sigleLegam || value.corpus || '',
      date: value.date || null,
      scriptoria: Array.isArray(value.scriptoria) ? value.scriptoria : [],
      scripta: Array.isArray(value.scripta) ? value.scripta : [],
      typeIndices,
      tokenIds: tokenIds.map(String),
      divisions: Array.isArray(value.divisions) ? value.divisions : [],
      languages: Array.isArray(value.languages) ? value.languages : [],
      headIds: Array.isArray(value.headIds) ? value.headIds : [],
      lemmaIds: Array.isArray(value.lemmaIds) ? value.lemmaIds : [],
      hccIds: Array.isArray(value.hccIds) ? value.hccIds : [],
      hasMetadata: Boolean(value.hasMetadata || value.languages),
    }
  }

  throw dataFormatError(`document ${documentId} is neither an object nor a tuple`)
}

function normalizeDocuments(payload) {
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') {
    throw dataFormatError('documents.json must contain an object')
  }
  return Object.fromEntries(
    Object.entries(payload).map(([id, value]) => [String(id), normalizeDocument(id, value)])
  )
}

function normalizeTokenMetadata(payload) {
  if (payload == null) {
    return {}
  }
  if (Array.isArray(payload) || typeof payload !== 'object') {
    throw dataFormatError('token_metadata.json must contain an object')
  }
  return Object.fromEntries(Object.entries(payload).map(([tokenId, value]) => {
    if (Array.isArray(value)) {
      return [String(tokenId), {
        languages: Array.isArray(value[0]) ? value[0] : [],
        headIds: Array.isArray(value[1]) ? value[1].map(String) : [],
        lemmaIds: Array.isArray(value[2]) ? value[2].map(String) : [],
        hccIds: Array.isArray(value[3]) ? value[3].map(String) : [],
      }]
    }
    if (value && typeof value === 'object') {
      return [String(tokenId), {
        languages: Array.isArray(value.languages) ? value.languages : [],
        headIds: Array.isArray(value.headIds) ? value.headIds.map(String) : [],
        lemmaIds: Array.isArray(value.lemmaIds) ? value.lemmaIds.map(String) : [],
        hccIds: Array.isArray(value.hccIds) ? value.hccIds.map(String) : [],
      }]
    }
    throw dataFormatError(`metadata for token ${tokenId} has an unsupported shape`)
  }))
}

function normalizeLookups(payload) {
  if (payload == null) {
    return {heads: [], corpora: [], lemmata: {}, hccs: {}}
  }
  if (Array.isArray(payload) || typeof payload !== 'object') {
    throw dataFormatError('lookups.json must contain an object')
  }
  return {
    heads: Array.isArray(payload.heads) ? payload.heads.map(head => ({
      id: String(head.id),
      form: head.form || '',
    })) : [],
    corpora: Array.isArray(payload.corpora) ? payload.corpora : [],
    lemmata: payload.lemmata && typeof payload.lemmata === 'object' ? payload.lemmata : {},
    hccs: payload.hccs && typeof payload.hccs === 'object' ? payload.hccs : {},
  }
}

function documentMatches(document, params, documentRegex, scriptoriaRegex) {
  if (params.corporaSet.size && !params.corporaSet.has(String(document.corpus))) {
    return false
  }
  if (documentRegex && !documentRegex.test(`${document.editionSigle} ${document.corpus || ''}`)) {
    return false
  }
  return !scriptoriaRegex || scriptoriaRegex.test(document.scriptoria.join(' '))
}

function hasFilterValue(value) {
  return value !== null && value !== undefined && value !== ''
}

function documentTokenMetadata(document, tokenId, tokenMetadata) {
  if (tokenMetadata[String(tokenId)]) {
    return tokenMetadata[String(tokenId)]
  }
  if (!documentTokenPositions.has(document)) {
    documentTokenPositions.set(
      document,
      new Map(document.tokenIds.map((id, index) => [String(id), index]))
    )
  }
  const position = documentTokenPositions.get(document).get(String(tokenId))
  if (position === undefined || !document.hasMetadata) {
    return {languages: [], headIds: [], lemmaIds: [], hccIds: []}
  }
  return {
    languages: document.languages[position] || [],
    headIds: (document.headIds[position] || []).map(String),
    lemmaIds: (document.lemmaIds[position] || []).map(String),
    hccIds: (document.hccIds[position] || []).map(String),
  }
}

function filterTokenIds(documentTokenIds, document, params, tokenMetadata) {
  const hasHeadFilter = hasFilterValue(params.headId)
  if (!hasHeadFilter && !params.languages.length && params.lemmatized === 'all' && !params.excludeLatin) {
    return documentTokenIds
  }
  if (hasHeadFilter && !Object.keys(tokenMetadata).length && !document.hasMetadata) {
    throw dataFormatError('the Head filter requires token_metadata.json; regenerate the export')
  }
  return documentTokenIds.filter(tokenId => {
    const metadata = documentTokenMetadata(document, tokenId, tokenMetadata)
    if (hasHeadFilter && metadata.headIds.includes(String(params.headId))) {
      return false
    }
    if (params.languages.length && !metadata.languages.some(
      language => params.languagesSet.has(String(language))
    )) {
      return false
    }
    if (params.excludeLatin && metadata.languages.includes('la')) {
      return false
    }
    if (params.lemmatized === 'lemmatized' && !metadata.lemmaIds.length) {
      return false
    }
    if (params.lemmatized === 'not lemmatized' && metadata.lemmaIds.length) {
      return false
    }
    return true
  })
}

function tokenIdsForType(typeIndex, typesDocs, documents, params, tokenMetadata, regexes) {
  const documentTokens = typesDocs[String(typeIndex)] || typesDocs[typeIndex] || {}
  if (Array.isArray(documentTokens) || typeof documentTokens !== 'object') {
    throw dataFormatError(`type ${typeIndex} document index has an unsupported shape`)
  }
  const result = []
  Object.entries(documentTokens).forEach(([documentId, tokenIds]) => {
    const document = documents[String(documentId)]
    if (!document) {
      throw dataFormatError(`type ${typeIndex} references missing document ${documentId}`)
    }
    if (!Array.isArray(tokenIds)) {
      throw dataFormatError(`type ${typeIndex} token list for document ${documentId} is not an array`)
    }
    if (!documentMatches(document, params, regexes.document, regexes.scriptoria)) {
      return
    }
    result.push(...filterTokenIds(tokenIds.map(String), document, params, tokenMetadata))
  })
  return result
}

function normalizeLemmaRecords(payload) {
  if (payload && payload.schemaVersion === 2 && Array.isArray(payload.records)) {
    return payload.records.map(record => {
      if (!record || !hasFilterValue(record.id) || typeof record.form !== 'string' ||
          !record.documentTokenIds || typeof record.documentTokenIds !== 'object') {
        throw dataFormatError('lemma_types.json contains an invalid version 2 record')
      }
      return {
        id: String(record.id),
        form: record.form,
        article: record.article || '',
        documentTokenIds: record.documentTokenIds,
      }
    })
  }
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') {
    throw dataFormatError('lemma_types.json must contain a version 1 object or version 2 records')
  }
  return Object.entries(payload).map(([form, value], index) => {
    if (!Array.isArray(value) || !Array.isArray(value[1])) {
      throw dataFormatError(`legacy lemma record ${form} has an unsupported shape`)
    }
    return {
      id: String(index),
      form,
      article: value[0] || '',
      typeIndices: value[1],
    }
  })
}

function searchStaticTokenIndex(index, rawParams) {
  if (!Array.isArray(index.types) || !index.typesDocs || typeof index.typesDocs !== 'object') {
    throw dataFormatError('types.json or types_docs_idxs.json has an unsupported shape')
  }
  const params = {
    ...rawParams,
    corpora: Array.isArray(rawParams.corpora) ? rawParams.corpora : [],
    languages: Array.isArray(rawParams.languages) ? rawParams.languages : [],
    lemmatized: rawParams.lemmatized || 'all',
  }
  params.corporaSet = new Set(params.corpora.map(String))
  params.languagesSet = new Set(params.languages.map(String))
  const regexes = {
    query: compileRegex(params.queryRegex, params.caseSensitive, 'query'),
    document: params.documents ? compileRegex(params.documents, params.caseSensitive, 'document') : null,
    scriptoria: params.scriptoria ? compileRegex(params.scriptoria, params.caseSensitive, 'scriptorium') : null,
  }

  if (params.searchBy === 'lemma') {
    return normalizeLemmaRecords(index.lemmaTypes).filter(
      lemma => regexes.query.test(lemma.form)
    ).map(lemma => {
      let tokenIds = []
      if (lemma.documentTokenIds) {
        Object.entries(lemma.documentTokenIds).forEach(([documentId, documentTokenIds]) => {
          const document = index.documents[String(documentId)]
          if (!document || !Array.isArray(documentTokenIds)) {
            throw dataFormatError(`lemma ${lemma.id} has an invalid document reference ${documentId}`)
          }
          if (documentMatches(document, params, regexes.document, regexes.scriptoria)) {
            tokenIds.push(...filterTokenIds(
              documentTokenIds.map(String), document, params, index.tokenMetadata
            ))
          }
        })
      } else {
        lemma.typeIndices.forEach(typeIndex => {
          tokenIds.push(...tokenIdsForType(
            typeIndex, index.typesDocs, index.documents, params, index.tokenMetadata, regexes
          ))
        })
      }
      tokenIds = [...new Set(tokenIds)]
      return {
        form: `${lemma.form}${lemma.article ? ` ${String(lemma.article).toUpperCase()}` : ''}`,
        lemma_id: lemma.id,
        tokenIds,
        count: tokenIds.length,
      }
    }).filter(result => result.count)
  }

  return index.types.map((form, typeIndex) => ({form, typeIndex})).filter(
    type => regexes.query.test(type.form)
  ).map(type => {
    const tokenIds = tokenIdsForType(
      type.typeIndex, index.typesDocs, index.documents, params, index.tokenMetadata, regexes
    )
    return {
      form: type.form,
      type_id: type.typeIndex,
      tokenIds,
      count: tokenIds.length,
    }
  }).filter(result => result.count)
}

export {
  dataFormatError,
  normalizeDocuments,
  normalizeLookups,
  normalizeTokenMetadata,
  searchStaticTokenIndex,
}
